import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { useUserSearch } from "@/graphql/hooks/useUserSearch"
import { useTournamentPlayerRegister } from "@/graphql/hooks/useTournamentPlayerRegister"
import { useToast } from "@/hooks/use-toast"
import { Search, UserPlus } from "lucide-react"

interface Person {
  id: number
  code: string
  email: string
  name: string
  fullName: string
}

interface Tournament {
  id: number
  TournamentOrganizers?: Array<{ Person: { id: number } }>
}

interface OrganizerSearchFormProps {
  tournament: Tournament
  onOrganizerRegistered: () => void
  className?: string
}

export const OrganizerSearchForm = ({
  tournament,
  onOrganizerRegistered,
}: OrganizerSearchFormProps) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedPersonId, setSelectedPersonId] = useState<string>("")
  const [searchResults, setSearchResults] = useState<Person[]>([])
  const [searchActive, setSearchActive] = useState(false)

  const { searchForUsers, loading: searchLoading } = useUserSearch()
  const { registerTournamentPlayer, loading: registerLoading } = useTournamentPlayerRegister()
  const { toast } = useToast()

  const enrolledOrganizerIds = tournament.TournamentOrganizers?.map((to) => to.Person?.id) || []

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!searchTerm.trim()) return

    try {
      const result = await searchForUsers({
        search: searchTerm,
        excludes: enrolledOrganizerIds,
        tournamentId: tournament.id,
        organizer: true,
      })

      if (result?.status?.success) {
        setSearchResults(result.Persons || [])
      } else {
        toast({
          title: "Error en la búsqueda",
          description: result?.status?.detail || "No se pudo realizar la búsqueda",
          variant: "destructive",
        })
      }
      setSearchActive(true)
    } catch (error) {
      toast({
        title: "Error en la búsqueda",
        description: "No se pudo realizar la búsqueda de organizadores",
        variant: "destructive",
      })
    }
  }

  const handleRegister = async () => {
    if (!selectedPersonId) return

    const registerData = {
      tournamentId: tournament.id,
      personId: parseInt(selectedPersonId),
      organizer: true,
    }

    try {
      const result = await registerTournamentPlayer(registerData)

      if (result?.status?.success) {
        const organizer = result.TournamentOrganizers?.find(
          (o: any) => o.personId === parseInt(selectedPersonId),
        )
        toast({
          title: "Organizador asociado exitosamente",
          variant: "success",
          description: `${organizer?.Person?.fullName || "El usuario"} (${organizer?.Person?.code || ""}) ha sido asociado como organizador`,
        })

        // Reset form
        setSearchTerm("")
        setSelectedPersonId("")
        setSearchResults([])
        setSearchActive(false)

        // Notify parent to refresh organizer list
        onOrganizerRegistered()
      } else {
        toast({
          title: "Error al asociar organizador",
          description: result?.status?.detail || "No se pudo asociar al organizador",
          variant: "destructive",
        })
      }
    } catch (error) {
      toast({
        title: "Error al asociar organizador",
        description: "No se pudo asociar al organizador al torneo",
        variant: "destructive",
      })
    }
  }

  const canRegister = !!selectedPersonId

  return (
    <Card className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
      <CardHeader>
        <CardTitle>Buscar y Asociar Organizador</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Search Form */}
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="search">Buscar organizador</Label>
            <div className="flex gap-2">
              <Input
                id="search"
                placeholder="Nombre, código o email del organizador"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={searchLoading}
                className="bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
              />
              <Button type="submit" disabled={searchLoading || !searchTerm.trim()}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </form>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="organizer-select">Seleccionar organizador</Label>
              <Select value={selectedPersonId} onValueChange={setSelectedPersonId}>
                <SelectTrigger className="bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                  <SelectValue placeholder="Selecciona un organizador" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                  {searchResults.map((person) => (
                    <SelectItem key={person.id} value={person.id.toString()}>
                      {person.fullName} ({person.code}) - {person.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Register Button */}
            <Button
              onClick={handleRegister}
              disabled={!canRegister || registerLoading}
              className="w-full"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              {registerLoading ? "Asociando..." : "Asociar Organizador"}
            </Button>
          </div>
        )}

        {searchResults.length === 0 && searchTerm && !searchLoading && searchActive && (
          <p className="text-center text-muted-foreground py-4 dark:text-gray-300">
            No se encontraron organizadores para "{searchTerm}"
          </p>
        )}
      </CardContent>
    </Card>
  )
}
